// Chart pattern detection: Double Bottom, Double Top, Head & Shoulders, Inverse H&S
// All algorithms are O(n) with a bounded lookback window — safe to run on 500+ candles.

export interface PatternCandle {
  time: number; // unix seconds
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export type PatternType = 'double_bottom' | 'double_top' | 'head_shoulders' | 'inv_head_shoulders';

export interface DetectedPattern {
  type: PatternType;
  signal: 'bull' | 'bear';
  confidence: number; // 0–100
  completionTime: number; // unix seconds — the candle where the second bottom/top/shoulder sits
  neckline: number;
  confirmed: boolean; // price has already closed through neckline
  description: string;
  scoreContribution: number; // +1 unconfirmed, +2 confirmed
}

export interface CandleQuality {
  totalCandles: number;
  zeroCandles: number;
  gapCount: number;
  gapPercent: number;
  isReliable: boolean;
  issues: string[];
}

// ── Timeframe helpers ─────────────────────────────────────────────────────────

const TF_SECS: Record<string, number> = {
  '1m': 60, '5m': 300, '15m': 900, '30m': 1800,
  '1h': 3600, '4h': 14400, '1D': 86400, '1W': 604800, '1M': 2592000,
};
export const timeframeToSecs = (tf: string): number => TF_SECS[tf] ?? 900;

// ── Pivot detection ───────────────────────────────────────────────────────────

// A candle is a pivot low if its low is the unique minimum in [i-period, i+period].
function pivotLows(candles: PatternCandle[], period: number): number[] {
  const out: number[] = [];
  for (let i = period; i < candles.length - period; i++) {
    const low = candles[i].low;
    let ok = true;
    for (let j = i - period; j <= i + period; j++) {
      if (j !== i && candles[j].low <= low) { ok = false; break; }
    }
    if (ok) out.push(i);
  }
  return out;
}

function pivotHighs(candles: PatternCandle[], period: number): number[] {
  const out: number[] = [];
  for (let i = period; i < candles.length - period; i++) {
    const high = candles[i].high;
    let ok = true;
    for (let j = i - period; j <= i + period; j++) {
      if (j !== i && candles[j].high >= high) { ok = false; break; }
    }
    if (ok) out.push(i);
  }
  return out;
}

// ── Prior-trend confirmation ──────────────────────────────────────────────────
// Reversal patterns (double top/bottom, H&S) only mean something if a genuine
// trend preceded them — geometry alone can match mid-chop, which is a known
// source of false signals. Require a real move into the first pivot before a
// pattern is allowed to count.

function hasPriorTrend(
  candles: PatternCandle[],
  pivotIdx: number,
  period: number,
  direction: 'up' | 'down',
): boolean {
  const lookback = period * 3;
  const startIdx = pivotIdx - lookback;
  if (startIdx < 0) return false; // not enough history to confirm a real trend preceded it

  const startPrice = candles[startIdx].close;
  const pivotPrice = direction === 'down' ? candles[pivotIdx].low : candles[pivotIdx].high;
  const movePct = direction === 'down'
    ? (startPrice - pivotPrice) / startPrice * 100   // price fell into the low
    : (pivotPrice - startPrice) / startPrice * 100;  // price rose into the high

  return movePct >= 1.5;
}

// ── Double Bottom (W pattern) ─────────────────────────────────────────────────

function detectDoubleBottom(
  candles: PatternCandle[],
  period: number,
  maxSpan: number,
): DetectedPattern[] {
  const lows = pivotLows(candles, period);
  const last = candles[candles.length - 1];
  const out: DetectedPattern[] = [];

  for (let i = 0; i < lows.length - 1; i++) {
    const b1i = lows[i], b2i = lows[i + 1];
    const span = b2i - b1i;
    if (span < period * 2 || span > maxSpan) continue;

    // Second bottom must be recent (within half the max span from the end)
    if (candles.length - 1 - b2i > maxSpan * 0.5) continue;

    // A double bottom only means a bullish reversal if a real downtrend preceded it
    if (!hasPriorTrend(candles, b1i, period, 'down')) continue;

    const b1 = candles[b1i].low, b2 = candles[b2i].low;
    const bottomDiff = Math.abs(b1 - b2) / Math.min(b1, b2) * 100;
    if (bottomDiff > 1.5) continue;

    // Peak between the two bottoms
    let peakH = 0;
    for (let k = b1i; k <= b2i; k++) peakH = Math.max(peakH, candles[k].high);
    const avgBottom = (b1 + b2) / 2;
    const peakPct = (peakH - avgBottom) / avgBottom * 100;
    if (peakPct < 1.5) continue;

    const neckline = peakH;
    const confirmed = last.close > neckline * 0.998;
    const conf = Math.round(
      (1 - bottomDiff / 1.5) * 35 +
      Math.min(peakPct / 4, 1) * 35 +
      (confirmed ? 30 : last.close > neckline * 0.99 ? 15 : 0),
    );
    if (conf < 40) continue;

    out.push({
      type: 'double_bottom',
      signal: 'bull',
      confidence: conf,
      completionTime: candles[b2i].time,
      neckline,
      confirmed,
      description: `Double Bottom ~${Math.round(avgBottom)} · Neckline ${Math.round(neckline)} · ${confirmed ? 'Confirmed ✓' : 'Watching'}`,
      scoreContribution: confirmed ? 2 : 1,
    });
  }
  return out;
}

// ── Double Top (M pattern) ────────────────────────────────────────────────────

function detectDoubleTop(
  candles: PatternCandle[],
  period: number,
  maxSpan: number,
): DetectedPattern[] {
  const highs = pivotHighs(candles, period);
  const last = candles[candles.length - 1];
  const out: DetectedPattern[] = [];

  for (let i = 0; i < highs.length - 1; i++) {
    const h1i = highs[i], h2i = highs[i + 1];
    const span = h2i - h1i;
    if (span < period * 2 || span > maxSpan) continue;
    if (candles.length - 1 - h2i > maxSpan * 0.5) continue;

    // A double top only means a bearish reversal if a real uptrend preceded it
    if (!hasPriorTrend(candles, h1i, period, 'up')) continue;

    const h1 = candles[h1i].high, h2 = candles[h2i].high;
    const topDiff = Math.abs(h1 - h2) / Math.max(h1, h2) * 100;
    if (topDiff > 1.5) continue;

    let troughL = Infinity;
    for (let k = h1i; k <= h2i; k++) troughL = Math.min(troughL, candles[k].low);
    const avgTop = (h1 + h2) / 2;
    const troughPct = (avgTop - troughL) / avgTop * 100;
    if (troughPct < 1.5) continue;

    const neckline = troughL;
    const confirmed = last.close < neckline * 1.002;
    const conf = Math.round(
      (1 - topDiff / 1.5) * 35 +
      Math.min(troughPct / 4, 1) * 35 +
      (confirmed ? 30 : last.close < neckline * 1.01 ? 15 : 0),
    );
    if (conf < 40) continue;

    out.push({
      type: 'double_top',
      signal: 'bear',
      confidence: conf,
      completionTime: candles[h2i].time,
      neckline,
      confirmed,
      description: `Double Top ~${Math.round(avgTop)} · Neckline ${Math.round(neckline)} · ${confirmed ? 'Confirmed ✓' : 'Watching'}`,
      scoreContribution: confirmed ? 2 : 1,
    });
  }
  return out;
}

// ── Head & Shoulders (bearish reversal) ──────────────────────────────────────

function detectHeadShoulders(
  candles: PatternCandle[],
  period: number,
  maxSpan: number,
): DetectedPattern[] {
  const highs = pivotHighs(candles, period);
  const last = candles[candles.length - 1];
  const out: DetectedPattern[] = [];

  for (let i = 0; i < highs.length - 2; i++) {
    const lsi = highs[i], hi = highs[i + 1], rsi = highs[i + 2];
    const span = rsi - lsi;
    if (span < period * 4 || span > maxSpan) continue;
    if (candles.length - 1 - rsi > maxSpan * 0.5) continue;

    // H&S only marks a bearish reversal if a real uptrend preceded the left shoulder
    if (!hasPriorTrend(candles, lsi, period, 'up')) continue;

    const lsH = candles[lsi].high;
    const headH = candles[hi].high;
    const rsH = candles[rsi].high;

    // Head must be meaningfully highest
    if (headH <= lsH || headH <= rsH) continue;
    if (headH < lsH * 1.003 || headH < rsH * 1.003) continue;

    // Shoulders should be similar in height
    const shoulderDiff = Math.abs(lsH - rsH) / Math.max(lsH, rsH) * 100;
    if (shoulderDiff > 2.0) continue;

    // Neckline = average of the two troughs flanking the head
    let nl1 = Infinity, nl2 = Infinity;
    for (let k = lsi; k <= hi; k++) nl1 = Math.min(nl1, candles[k].low);
    for (let k = hi; k <= rsi; k++) nl2 = Math.min(nl2, candles[k].low);
    const neckline = (nl1 + nl2) / 2;

    const headHeight = headH - neckline;
    if (headHeight / headH < 0.01) continue; // head must be at least 1% above neckline

    const confirmed = last.close < neckline * 1.002;
    const conf = Math.round(
      (1 - shoulderDiff / 2) * 35 +
      Math.min(headHeight / headH / 0.03, 1) * 35 +
      (confirmed ? 30 : last.close < neckline * 1.015 ? 15 : 0),
    );
    if (conf < 40) continue;

    out.push({
      type: 'head_shoulders',
      signal: 'bear',
      confidence: conf,
      completionTime: candles[rsi].time,
      neckline,
      confirmed,
      description: `H&S Head ${Math.round(headH)} · Neckline ${Math.round(neckline)} · ${confirmed ? 'Confirmed ✓' : 'Watching'}`,
      scoreContribution: confirmed ? 2 : 1,
    });
  }
  return out;
}

// ── Inverse Head & Shoulders (bullish reversal) ───────────────────────────────

function detectInvHeadShoulders(
  candles: PatternCandle[],
  period: number,
  maxSpan: number,
): DetectedPattern[] {
  const lows = pivotLows(candles, period);
  const last = candles[candles.length - 1];
  const out: DetectedPattern[] = [];

  for (let i = 0; i < lows.length - 2; i++) {
    const lsi = lows[i], hi = lows[i + 1], rsi = lows[i + 2];
    const span = rsi - lsi;
    if (span < period * 4 || span > maxSpan) continue;
    if (candles.length - 1 - rsi > maxSpan * 0.5) continue;

    // Inverse H&S only marks a bullish reversal if a real downtrend preceded the left shoulder
    if (!hasPriorTrend(candles, lsi, period, 'down')) continue;

    const lsL = candles[lsi].low;
    const headL = candles[hi].low;
    const rsL = candles[rsi].low;

    if (headL >= lsL || headL >= rsL) continue;
    if (headL > lsL * 0.997 || headL > rsL * 0.997) continue;

    const shoulderDiff = Math.abs(lsL - rsL) / Math.min(lsL, rsL) * 100;
    if (shoulderDiff > 2.0) continue;

    let nl1 = 0, nl2 = 0;
    for (let k = lsi; k <= hi; k++) nl1 = Math.max(nl1, candles[k].high);
    for (let k = hi; k <= rsi; k++) nl2 = Math.max(nl2, candles[k].high);
    const neckline = (nl1 + nl2) / 2;

    const headDepth = neckline - headL;
    if (headDepth / neckline < 0.01) continue;

    const confirmed = last.close > neckline * 0.998;
    const conf = Math.round(
      (1 - shoulderDiff / 2) * 35 +
      Math.min(headDepth / neckline / 0.03, 1) * 35 +
      (confirmed ? 30 : last.close > neckline * 0.985 ? 15 : 0),
    );
    if (conf < 40) continue;

    out.push({
      type: 'inv_head_shoulders',
      signal: 'bull',
      confidence: conf,
      completionTime: candles[rsi].time,
      neckline,
      confirmed,
      description: `Inv H&S Head ${Math.round(headL)} · Neckline ${Math.round(neckline)} · ${confirmed ? 'Confirmed ✓' : 'Watching'}`,
      scoreContribution: confirmed ? 2 : 1,
    });
  }
  return out;
}

// ── Candle quality check ──────────────────────────────────────────────────────

export function validateCandleQuality(
  candles: PatternCandle[],
  timeframeSecs: number,
): CandleQuality {
  if (!candles.length) {
    return { totalCandles: 0, zeroCandles: 0, gapCount: 0, gapPercent: 0, isReliable: false, issues: ['No data'] };
  }

  let zeroCandles = 0, gapCount = 0;
  for (const c of candles) {
    if (c.open <= 0 || c.high <= 0 || c.low <= 0 || c.close <= 0) zeroCandles++;
  }
  for (let i = 1; i < candles.length; i++) {
    // Gap = missing candle(s). Allow 3× timeframe to cover market breaks / weekends on intraday.
    if (candles[i].time - candles[i - 1].time > timeframeSecs * 3) gapCount++;
  }
  const gapPercent = candles.length > 1
    ? Math.round((gapCount / (candles.length - 1)) * 100)
    : 0;

  const issues: string[] = [];
  if (zeroCandles > 0) issues.push(`${zeroCandles} zero-value candle${zeroCandles > 1 ? 's' : ''}`);
  if (gapPercent > 10) issues.push(`${gapPercent}% time gaps`);
  if (candles.length < 30) issues.push('Too few candles');

  return { totalCandles: candles.length, zeroCandles, gapCount, gapPercent, isReliable: issues.length === 0, issues };
}

// ── Main entry point ──────────────────────────────────────────────────────────

export function detectChartPatterns(
  candles: PatternCandle[],
  timeframe: string,
): DetectedPattern[] {
  if (candles.length < 20) return [];

  const isDaily = timeframe === '1D' || timeframe === '1W' || timeframe === '1M';
  // period = min neighbours on each side for a pivot to be valid
  const period = isDaily ? 3 : timeframe === '5m' ? 6 : 5;
  // maxSpan = max candle count between the two legs of a pattern
  const maxSpan = isDaily ? 35 : 70;

  const all: DetectedPattern[] = [
    ...detectDoubleBottom(candles, period, maxSpan),
    ...detectDoubleTop(candles, period, maxSpan),
    ...detectHeadShoulders(candles, period, maxSpan),
    ...detectInvHeadShoulders(candles, period, maxSpan),
  ];

  // Keep only the highest-confidence instance of each type (one of each per chart)
  const best = new Map<PatternType, DetectedPattern>();
  for (const p of all) {
    const existing = best.get(p.type);
    if (!existing || p.confidence > existing.confidence) best.set(p.type, p);
  }

  return Array.from(best.values()).sort((a, b) => b.confidence - a.confidence);
}

// ── Label helpers used by chart + panel ──────────────────────────────────────

export const PATTERN_LABEL: Record<PatternType, string> = {
  double_bottom: '2B',
  double_top: '2T',
  head_shoulders: 'H&S',
  inv_head_shoulders: 'iH&S',
};

export const PATTERN_FULL_NAME: Record<PatternType, string> = {
  double_bottom: 'Double Bottom',
  double_top: 'Double Top',
  head_shoulders: 'Head & Shoulders',
  inv_head_shoulders: 'Inv Head & Shoulders',
};

// Plain-English "what this typically means" text shown in the pattern insights panel
export const PATTERN_EXPLANATION: Record<PatternType, string> = {
  double_bottom: 'Bullish reversal setup — two lows near the same level suggest sellers are losing control. A close above the neckline often precedes a move higher; failing to hold above it invalidates the setup.',
  double_top: 'Bearish reversal setup — two highs near the same level suggest buyers are losing control. A close below the neckline often precedes a move lower; failing to hold below it invalidates the setup.',
  head_shoulders: 'Bearish reversal setup — a higher high (head) that fails to hold, flanked by two similar lower highs (shoulders), often precedes a breakdown below the neckline.',
  inv_head_shoulders: 'Bullish reversal setup — a lower low (head) that fails to hold, flanked by two similar higher lows (shoulders), often precedes a breakout above the neckline.',
};
